import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, Loader2, RefreshCcw, ChevronDown, User, ArrowRight, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { useCollapse } from "../../lib/cockpitCollapse";

/**
 * Jul-2026 (User-Wunsch) — Verkaufs-Cockpit: Live-Übersicht aller aktiven
 * 11-Schritte-Verkaufsprozesse (Disposition) mit Fortschrittsbalken und dem
 * nächsten offenen Schritt (Blocker). Klick öffnet die Disposition mit
 * vorbefüllter Suche zum Fahrzeug.
 */
export default function SalesCockpitCard() {
    const [data, setData] = useState({ items: [], count: 0, completed_count: 0 });
    const [loading, setLoading] = useState(true);
    const [collapsed, toggleCollapsed] = useCollapse("sales_cockpit", false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/disposition/cockpit");
            setData(r.data || { items: [] });
        } catch { /* ignoriert */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const items = data.items || [];
    const stuck = items.filter((i) => (i.days ?? 0) > 14).length;
    const fmtEur = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(v) || 0);

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="sales-cockpit-card">
            <button
                type="button"
                onClick={toggleCollapsed}
                className="w-full flex items-center gap-2 px-4 py-3 text-left"
                data-testid="sales-cockpit-toggle"
            >
                <Gauge className="w-5 h-5 text-emerald-600 shrink-0" />
                <span className="font-display font-black text-base text-[#111827]">Verkaufs-Cockpit</span>
                <span className="text-[11px] text-gray-500">laufende Verkäufe (11 Schritte)</span>
                {data.count > 0 && (
                    <span className="ml-1 text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-sm px-1.5 py-0.5" data-testid="sales-cockpit-count">
                        {data.count} aktiv
                    </span>
                )}
                {Number(data.open_revenue) > 0 && (
                    <span className="text-[10px] font-bold bg-[#111827] text-[#FFCC00] rounded-sm px-1.5 py-0.5" data-testid="sales-cockpit-revenue" title="Summe der erwarteten Verkaufserlöse aller laufenden Abwicklungen">
                        ∑ {fmtEur(data.open_revenue)} offen
                    </span>
                )}
                {stuck > 0 && (
                    <span className="text-[10px] font-bold bg-red-100 text-red-800 border border-red-300 rounded-sm px-1.5 py-0.5" data-testid="sales-cockpit-stuck">
                        <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />
                        {stuck} &gt; 14 Tage
                    </span>
                )}
                <span className="flex-1" />
                <RefreshCcw
                    className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700"
                    onClick={(e) => { e.stopPropagation(); load(); }}
                    data-testid="sales-cockpit-refresh"
                />
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${collapsed ? "" : "rotate-180"}`} />
            </button>
            {!collapsed && (
                <div className="px-4 pb-4">
                    {loading ? (
                        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                    ) : items.length === 0 ? (
                        <div className="text-sm text-gray-400 italic py-2" data-testid="sales-cockpit-empty">
                            Keine laufenden Verkaufsprozesse — alle Abwicklungen sind abgeschlossen.
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 border border-gray-100 rounded-sm">
                            {items.map((it) => {
                                const pct = Math.round((it.progress_done / (it.progress_total || 11)) * 100);
                                const urgent = (it.days ?? 0) > 14;
                                return (
                                    <div key={it.id} className="flex items-center gap-3 px-3 py-2" data-testid={`sales-cockpit-row-${it.id}`}>
                                        {it.image
                                            ? <img src={it.image} alt="" className="w-10 h-10 object-cover rounded-sm shrink-0" />
                                            : <div className="w-10 h-10 bg-gray-100 rounded-sm shrink-0" />}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold text-sm truncate">{it.title}</span>
                                                {it.internal_number && <span className="font-mono-num text-[10px] text-gray-500">#{it.internal_number}</span>}
                                                {it.site_code && <span className="text-[9px] font-bold bg-gray-100 border border-gray-200 rounded-sm px-1">{it.site_code}</span>}
                                                <span className={`text-[9px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${it.sold ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                                                    {it.sold ? "Verkauft" : "Reserviert"}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <div className="h-1.5 bg-gray-100 rounded-full flex-1 max-w-[160px] overflow-hidden">
                                                    <div className={`h-full rounded-full ${pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-red-400"}`} style={{ width: `${Math.max(pct, 4)}%` }} />
                                                </div>
                                                <span className="font-mono-num text-[11px] font-bold text-gray-700" data-testid={`sales-cockpit-progress-${it.id}`}>
                                                    {it.progress_done}/{it.progress_total}
                                                </span>
                                                {it.next_step && (
                                                    <span className="text-[10px] font-bold text-orange-800 bg-orange-50 border border-orange-200 rounded-sm px-1.5 py-0.5 truncate" title={`Offen: ${(it.open_steps || []).join(", ")}`} data-testid={`sales-cockpit-blocker-${it.id}`}>
                                                        Nächster Schritt: {it.next_step}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-500">
                                                {it.buyer && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{it.buyer}</span>}
                                                {it.seller && <span>Verkäufer: {it.seller}</span>}
                                                {Number(it.expected_revenue) > 0 && (
                                                    <span className="font-bold text-gray-700" data-testid={`sales-cockpit-revenue-${it.id}`}>{fmtEur(it.expected_revenue)}</span>
                                                )}
                                                {it.days != null && (
                                                    <span className={urgent ? "text-red-700 font-bold" : ""}>{it.days} Tag{it.days === 1 ? "" : "e"} in Abwicklung</span>
                                                )}
                                            </div>
                                        </div>
                                        <Link
                                            to={`/dealer/disposition?q=${encodeURIComponent(it.search_key || "")}`}
                                            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:text-emerald-900 border border-emerald-300 hover:bg-emerald-50 rounded-sm px-2 py-1"
                                            data-testid={`sales-cockpit-open-${it.id}`}
                                        >
                                            Öffnen <ArrowRight className="w-3 h-3" />
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {data.completed_count > 0 && !loading && (
                        <p className="text-[10px] text-gray-400 mt-2">{data.completed_count} abgeschlossene Abwicklungen werden ausgeblendet.</p>
                    )}
                </div>
            )}
        </div>
    );
}
